/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TablePagination } from 'src/core/components/TablePagination';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { numberToPercentageString, truncateValue } from 'src/core/utils/number';
import { PerformanceMetricsBreakdown } from 'src/modules/modelDetails/stores/ModelEvaluationStore';

export type ModelEvaluationTableProps = TableProps<PerformanceMetricsBreakdown> & {
    tableTitle: string;
    nameColumnTitle: string;
};

const messages = defineMessages({
    recall: { id: 'modelEvaluationTable.recall', defaultMessage: 'Recall %' },
    f1Score: { id: 'modelEvaluationTable.f1Score', defaultMessage: 'F1 score' },
    precision: { id: 'modelEvaluationTable.precision', defaultMessage: 'Precision %' },
    numberOfTags: { id: 'modelEvaluationTable.numberOfTags', defaultMessage: 'Number of labels' }
});

const paginationStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { margin: '10px 0 !important' } });

/**
 * Displays a list of a trained model's entities or classes along
 * with their individual performance matrix.
 */
export const ModelEvaluationTable = (props: ModelEvaluationTableProps) => {
    const { tableTitle, nameColumnTitle, items, sortKey, ...rest } = props;
    const { localize } = useLocalization();
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(items, 7);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    React.useEffect(() => {
        setCurrentPage(0);
    }, [tableTitle]);

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 300,
            maxWidth: 550,
            isRowHeader: true,
            isResizable: true,
            fieldName: 'name',
            name: nameColumnTitle,
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc'
        },
        {
            minWidth: 100,
            maxWidth: 150,
            key: 'precision',
            isResizable: true,
            isRowHeader: false,
            fieldName: 'precision',
            name: localize(messages.precision),
            isSorted: sortKey?.key === 'precision',
            isSortedDescending: sortKey?.key === 'precision' && sortKey?.order === 'desc',
            onRender: (item: PerformanceMetricsBreakdown) => numberToPercentageString({ ratio: item.precision, showPercentage: false })
        },
        {
            key: 'recall',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            isRowHeader: false,
            fieldName: 'recall',
            name: localize(messages.recall),
            isSorted: sortKey?.key === 'recall',
            isSortedDescending: sortKey?.key === 'recall' && sortKey?.order === 'desc',
            onRender: (item: PerformanceMetricsBreakdown) => numberToPercentageString({ ratio: item.recall, showPercentage: false })
        },
        {
            key: 'f1Score',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            isRowHeader: false,
            fieldName: 'f1Score',
            name: localize(messages.f1Score),
            isSorted: sortKey?.key === 'f1Score',
            isSortedDescending: sortKey?.key === 'f1Score' && sortKey?.order === 'desc',
            onRender: (item: PerformanceMetricsBreakdown) => truncateValue(item.f1Score, 3)
        }
    ];

    return (
        <Stack>
            <LuisText variant="mediumPlus" fontWeight={600}>
                {tableTitle}
            </LuisText>
            <Table items={pageItems} columns={columns} selectionMode={SelectionMode.none} {...rest} />

            <TablePagination
                pageCount={pageCount}
                currentPage={currentPage}
                containerStyles={paginationStyles}
                onPageChange={p => handlePageChange(p.selected)}
            />
        </Stack>
    );
};
